/**
 * The rules that suggest an existing offer from the save (MONETISATION_PLAN MP-05; balance/src/offers.json `triggers`).
 * A suggestion is a badge on something already on sale, never a pop-up: meta/offerTriggers.ts holds the rules, the
 * app's offerTriggers.ts reads them against the save and the clock.
 */
export interface OfferTriggerRules {
  /** Losses of one level in a row (since its last win) that suggest gear: today's Armourer deals or a Chapter Pack. */
  readonly lossStreak: number;
  /** The gear suggestion lasts this many UTC days after the last of those losses. */
  readonly lossDays: number;
  /** From this highest cleared level, gold below the next upgrade's cost suggests the Shop's gold packs. */
  readonly goldFromLevel: number;
  /** Reaching this level (the Mythic rarity's first, chests.json rarityClimb) suggests the Master's Collection, once a chapter. */
  readonly mythicFromLevel: number;
  /** The Welcome Back pack: on sale this many hours from the return, once per return, at most once every `everyDays`. */
  readonly welcomeHours: number;
  readonly welcomeEveryDays: number;
}

/** A bundle without `triggers` suggests nothing (lossStreak 0 is off, the level gates are out of reach). */
export const NO_OFFER_TRIGGERS: OfferTriggerRules = { lossStreak: 0, lossDays: 0, goldFromLevel: Infinity, mythicFromLevel: Infinity, welcomeHours: 0, welcomeEveryDays: 0 };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function whole(v: Json, key: string): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= 0) return v;
  throw new TypeError(`offers.triggers.${key}: expected a whole number, got ${JSON.stringify(v)}`);
}

/** offers.json `triggers` (gen.py has validated it; this re-checks the shape). */
export function parseOfferTriggers(t: Json): OfferTriggerRules {
  if (t === undefined) return NO_OFFER_TRIGGERS;
  return {
    lossStreak: whole(t.lossStreak, "lossStreak"), lossDays: whole(t.lossDays, "lossDays"), goldFromLevel: whole(t.goldFromLevel, "goldFromLevel"),
    mythicFromLevel: whole(t.mythicFromLevel, "mythicFromLevel"), welcomeHours: whole(t.welcome?.hours, "welcome.hours"),
    welcomeEveryDays: whole(t.welcome?.everyDays, "welcome.everyDays"),
  };
}
